<script setup lang="ts">
    import { InputRow, MultiCombobox, Select, Combobox } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import DeleteButton from '@/components/DeleteButton.vue';
    import { optionsDays, optionsYesNo } from '@/utils/form';

    const props = defineProps<{
        form: any;
        mainForm: any;
        index: number;
        keepersEmployees: any[];
    }>();

    const emit = defineEmits<{
        remove: [index: number];
    }>();

    const removeTask = () => {
        emit('remove', props.index);
    };

    const statusOptions = [
        { value: 'Open', label: 'task-templates.statuses.Open' },
        { value: 'Finish', label: 'task-templates.statuses.Finish' },
        { value: 'Closed', label: 'task-templates.statuses.Closed' },
        { value: 'Recurring', label: 'task-templates.statuses.Recurring' },
    ];

    const frequencyOptions = [
        { value: 0, label: 'task-templates.frequency_options.0' },
        { value: 1, label: 'task-templates.frequency_options.1' },
        { value: 2, label: 'task-templates.frequency_options.2' },
        { value: 4, label: 'task-templates.frequency_options.4' },
    ];

    const oddOrEvenWeekOptions = [
        { value: 1, label: 'task-templates.odd_or_even_week_options.1' },
        { value: 0, label: 'task-templates.odd_or_even_week_options.0' },
    ];

    const startOrEndOfMonthOptions = [
        { value: 1, label: 'task-templates.start_or_end_month_options.1' },
        { value: 0, label: 'task-templates.start_or_end_month_options.0' },
    ];
</script>

<template>
    <div class="border-secondary grid grid-cols-1 gap-3 rounded-md border p-5 sm:grid-cols-2">
        <div class="col-span-2 flex items-center justify-between gap-3">
            <span class="text-secondary text-xs">{{ $t('task-templates.task.task') }}</span>
            <DeleteButton @click.prevent="removeTask" />
        </div>
        <div class="flex flex-col gap-3">
            <InputRow
                :form="mainForm"
                name="title"
                label="task-templates.task.title"
                :error-key="`tasks.${index}.title`"
            >
                <Input
                    id="title"
                    v-model="form.title"
                    :placeholder="$t('task-templates.task.title')"
                />
            </InputRow>
            <InputRow
                :form="mainForm"
                name="description"
                label="task-templates.task.description"
                :error-key="`tasks.${index}.description`"
            >
                <Input
                    id="description"
                    v-model="form.description"
                    :placeholder="$t('task-templates.task.description')"
                />
            </InputRow>
            <InputRow
                :form="mainForm"
                name="weekdays"
                label="task-templates.weekdays"
                :error-key="`tasks.${index}.weekdays`"
            >
                <MultiCombobox
                    v-model="form.weekdays"
                    :options="optionsDays"
                    :translate="true"
                    :placeholder="$t('task-templates.weekdays')"
                />
            </InputRow>
            <InputRow
                :form="mainForm"
                name="status"
                label="task-templates.task.status"
            >
                <Select
                    v-model="form.status"
                    :options="statusOptions"
                    :translate="true"
                    :placeholder="$t('task-templates.task.status')"
                />
            </InputRow>
        </div>
        <div class="flex flex-col gap-2">
            <Label>{{ $t('task-templates.task.task_notification') }}</Label>
            <div class="border-secondary flex flex-col gap-3 rounded-md border p-3">
                <InputRow
                    :form="mainForm"
                    name="send_notification"
                    label="task-templates.task.send_notification"
                >
                    <Select
                        v-model="form.send_notification"
                        :options="optionsYesNo"
                        :translate="true"
                    />
                </InputRow>
                <InputRow
                    :form="mainForm"
                    name="notification_recurring_week"
                    label="task-templates.task.notification_frequency"
                    :error-key="`tasks.${index}.notification_recurring_week`"
                >
                    <Select
                        v-model="form.notification_recurring_week"
                        :options="frequencyOptions"
                        :translate="true"
                    />
                </InputRow>
                <InputRow
                    v-if="form.notification_recurring_week === '2'"
                    :form="mainForm"
                    name="notification_recurring_even_week"
                    label="task-templates.task.notification_odd_or_even_week"
                >
                    <Select
                        v-model="form.notification_recurring_even_week"
                        :options="oddOrEvenWeekOptions"
                        :translate="true"
                    />
                </InputRow>
                <InputRow
                    v-if="form.notification_recurring_week === '4'"
                    :form="mainForm"
                    name="notification_recurring_start_of_month"
                    label="task-templates.task.notification_start_or_end_month"
                >
                    <Select
                        v-model="form.notification_recurring_start_of_month"
                        :options="startOrEndOfMonthOptions"
                        :translate="true"
                    />
                </InputRow>
                <InputRow
                    :form="mainForm"
                    name="responsible_id"
                    label="task-templates.responsible"
                >
                    <Combobox
                        v-model="form.responsible_id"
                        :options="keepersEmployees"
                        :placeholder="$t('task-templates.responsible')"
                        :loading="!keepersEmployees.length"
                        label-by="name"
                        value-by="id"
                    />
                </InputRow>
            </div>
        </div>
    </div>
</template>
